import {createReducer} from "redux-act";
import {IDictionary} from "modules/types";
import {PlayerStatus, RequestState} from "modules/enums";
import {
	requestPlayers,
	requestPlayersFailed,
	requestPlayersStatsSuccess,
	requestPlayersSuccess,
	requestSinglePlayerStats,
	requestSinglePlayerStatsSuccess,
	singlePlayerStatsClear,
} from "modules/actions";

export interface IPlayerStats {
	totalPoints: number;
	avgPoints: number;
	gamesPlayed: number;
	roundScores: IDictionary<number> | null;
	pickedBy: number;
	goals: number;
	assists: number;
	redCard: number;
	yellowCard: number;
	ownGoals: number;
	penaltySave: number;
	penaltyMissed: number;
	starting11: number;
	substitutions: number;
	cleanSheets: number;
	goalsConceded: number;
}

export interface IPlayer {
	id: number;
	name: string;
	preferredName: string;
	squadId: number;
	cost: number;
	status: PlayerStatus;
	position: number;
	locked: number;
	stats: IPlayerStats;
	matchDayPoints: IDictionary<number | null>;
	feedId: number;
	firstName: string;
}

export interface IPlayerProfileStats {
	total_points: number;
	avg_points: number;
	games_played: number;
	goals: number;
	yellow_card: number;
	red_card: number;
	penalty_missed: number;
	own_goals: number;
	assists: number;
	penalty_save: number;
	starting11: number;
	substitutions: number;
	clean_sheets: number;
	goals_conceded: number;
}

export type IPlayerProfileStatsRecord = Record<number, IPlayerProfileStats>;

export interface IPlayerReducer {
	requestState: RequestState;
	players: IPlayer[];
	singlePlayerStat: null | IPLayerSingleStats[];
	playersStats: IPlayerProfileStatsRecord;
}

export interface IPLayerSingleStats {
	round: number;
	tournamentId: number;
	points: number;
	stats: {
		AS: number;
		GC: number;
		GS: number;
		MP: number;
		OG: number;
		RC: number;
		CP: number;
		SP: number;
		YC: number;
		CS: number;
		MIN: number;
		SXI: boolean;
		SUB_IN: boolean;
		FD: number;
		INT: number;
		GFK: number;
		SV: number;
		TCK: number;
		CLR: number;
		BL: number;
		KP: number;
		SOT: number;
		CC: number;
		SB: number;
		PW: number;
	};
}

const initial: IPlayerReducer = {
	players: [],
	playersStats: {},
	singlePlayerStat: null,
	requestState: RequestState.IDLE,
};

const onRequestPlayers = (state: IPlayerReducer) => ({
	...state,
	requestState: RequestState.Requested,
});

const onRequestPlayersSuccess = (state: IPlayerReducer, payload: IPlayer[]) => ({
	...state,
	requestState: RequestState.Received,
	players: payload,
});

const onRequestPlayersFailed = (state: IPlayerReducer) => ({
	...state,
	requestState: RequestState.Error,
});

const onRequestPlayersStatsSuccess = (
	state: IPlayerReducer,
	payload: IPlayerProfileStatsRecord
) => ({
	...state,
	playersStats: payload,
});

const onRequestSinglePlayerStats = (state: IPlayerReducer) => ({
	...state,
	requestState: RequestState.Requested,
});

const onRequestSinglePlayerStatsSuccess = (
	state: IPlayerReducer,
	payload: IPLayerSingleStats[]
) => ({
	...state,
	singlePlayerStat: payload,
	requestState: RequestState.IDLE,
});

const onSinglePlayerStatsClear = (state: IPlayerReducer) => ({
	...state,
	singlePlayerStat: null,
	requestState: RequestState.IDLE,
});

export const players = createReducer<IPlayerReducer>({}, initial)
	.on(requestPlayers, onRequestPlayers)
	.on(requestPlayersSuccess, onRequestPlayersSuccess)
	.on(requestPlayersFailed, onRequestPlayersFailed)
	.on(requestPlayersStatsSuccess, onRequestPlayersStatsSuccess)
	.on(requestSinglePlayerStats, onRequestSinglePlayerStats)
	.on(requestSinglePlayerStatsSuccess, onRequestSinglePlayerStatsSuccess)
	.on(singlePlayerStatsClear, onSinglePlayerStatsClear);
